import {
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays
} from 'date-fns';
import { buildEntriesForChartSelection, isDrilldownableCode } from '../nodes/nodeFilterUtils';

/**
 * Расчёты раздела «Типы и итоги звонков». Сами числа считает сервер: здесь только разбор
 * его ответов, раскладка по периодам и подписи.
 */

/** Строка «Без оператора» в ответе разбора: журнал такой id не понимает. */
export const NO_EMPLOYEE_ID = '00000000-0000-0000-0000-000000000000';

const DAY_KEY_FORMAT = 'yyyy-MM-dd';
const WEEK_THRESHOLD_DAYS = 14;
const MONTH_THRESHOLD_DAYS = 90;

export const hasOperator = (employee) => employee.employeeId !== NO_EMPLOYEE_ID;

/**
 * Выходы раздела в каталоге пакета. Итог ищется сначала в той же ноде, что и тип: в пакете может
 * быть несколько триажей, и пара из разных нод считалась бы по разным наборам звонков.
 */
export const findCallCharts = (catalog) => {
  const charts = catalog?.charts ?? [];
  const typeChart = charts.find((chart) => chart.outputKey === 'call_type') ?? null;
  const outcomeChart =
    charts.find((chart) => chart.outputKey === 'call_outcome' && chart.nodeId === typeChart?.nodeId)
    ?? charts.find((chart) => chart.outputKey === 'call_outcome')
    ?? null;

  return { typeChart, outcomeChart };
};

/** Строки агрегата, по которым можно идти дальше: служебные значения в разбор не ведут. */
export const readCategoryRows = (aggregate) =>
  (aggregate?.items ?? [])
    .filter((item) => isDrilldownableCode(item.code) && item.code !== 'unknown' && Number(item.calls) > 0)
    .map((item) => ({
      code: String(item.code),
      label: item.label || String(item.code),
      calls: Number(item.calls)
    }))
    .sort((a, b) => b.calls - a.calls);

export const categoryFilter = (chart, code) => buildEntriesForChartSelection(chart, code).join(';');

export const pairFilter = (typeChart, outcomeChart, typeCode, outcomeCode) =>
  [
    ...buildEntriesForChartSelection(typeChart, typeCode),
    ...buildEntriesForChartSelection(outcomeChart, outcomeCode)
  ].join(';');

/**
 * Выбранная пара. В режиме «по типам» итог выбирается из разбивки типа, в режиме «по итогам» —
 * наоборот, и число звонков пары лежит в той разбивке, из которой выбирали.
 */
export const resolvePair = ({ mode, type, outcome, typeOutcomes = [], outcomeTypes = [] }) => {
  if (!type || !outcome) {
    return null;
  }

  const source = mode === 'outcomes'
    ? outcomeTypes.find(({ code }) => code === type.code)
    : typeOutcomes.find(({ code }) => code === outcome.code);

  return source
    ? { key: `${type.code}:${outcome.code}`, type, outcome, calls: source.calls }
    : null;
};

export const defaultGranularity = (dateRange) => {
  const days = dateRange?.from && dateRange?.to
    ? differenceInCalendarDays(dateRange.to, dateRange.from) + 1
    : 0;

  if (days > MONTH_THRESHOLD_DAYS) {
    return 'month';
  }

  return days > WEEK_THRESHOLD_DAYS ? 'week' : 'day';
};

/** Ключ периода совпадает с серверным: начало периода, неделя — с понедельника. */
export const bucketKey = (date, granularity) => {
  if (granularity === 'week') {
    return format(startOfWeek(date, { weekStartsOn: 1 }), DAY_KEY_FORMAT);
  }

  return format(granularity === 'month' ? startOfMonth(date) : date, DAY_KEY_FORMAT);
};

const rangeDays = (dateRange) =>
  dateRange?.from && dateRange?.to && dateRange.from <= dateRange.to
    ? eachDayOfInterval({ start: dateRange.from, end: dateRange.to })
    : [];

export const rangeBucketKeys = (dateRange, granularity) => [
  ...new Set(rangeDays(dateRange).map((date) => bucketKey(date, granularity)))
];

export const formatBucketLabel = (bucket, granularity) =>
  format(parseISO(bucket), granularity === 'month' ? 'MM.yyyy' : 'dd.MM');

/** Предыдущий период той же длины, непосредственно перед текущим. */
export const previousPeriod = (dateRange) => {
  const days = rangeDays(dateRange).length;

  return days > 0
    ? { days, from: subDays(dateRange.from, days), to: subDays(dateRange.from, 1) }
    : null;
};

const pointDay = (point) => String(point.bucket).slice(0, 10);

/**
 * Динамика всех значений выхода. Ключи серий — по индексу, а не по коду: recharts читает точку
 * в `dataKey` как путь внутрь объекта, а коды пакета её содержать могут.
 */
export const buildCategoryTrend = ({ points, items, dateRange, granularity }) => {
  const seriesIndex = new Map(items.map(({ code }, index) => [code, index]));
  const rows = new Map(
    rangeBucketKeys(dateRange, granularity).map((bucket) => {
      const row = { bucket, label: formatBucketLabel(bucket, granularity) };
      items.forEach((_, index) => {
        row[`calls_${index}`] = 0;
        row[`share_${index}`] = 0;
      });
      return [bucket, row];
    })
  );

  (points ?? []).forEach((point) => {
    const row = rows.get(pointDay(point));
    const index = seriesIndex.get(point.code);
    if (row && index !== undefined) {
      row[`calls_${index}`] = Number(point.calls) || 0;
      row[`share_${index}`] = Number(point.share) || 0;
    }
  });

  return [...rows.values()];
};

const callsByDay = (points, code) =>
  new Map(
    (points ?? [])
      .filter((point) => point.code === code)
      .map((point) => [pointDay(point), Number(point.calls) || 0])
  );

const sumDays = (byDay, dates) =>
  dates.reduce((sum, date) => sum + (byDay.get(format(date, DAY_KEY_FORMAT)) ?? 0), 0);

/** Доля в процентах; null при нулевом знаменателе: она не определена, а не равна нулю. */
const shareOf = (part, total) => (total > 0 ? Math.round((part / total) * 1000) / 10 : null);

/**
 * Динамика пары «тип → итог» с наложением прошлого периода. Запрашивается по дням, а по неделям и
 * месяцам складывается здесь: день текущего периода сопоставляется с днём прошлого по порядку,
 * и календарные недели двух периодов иначе не совпали бы по составу дней.
 */
export const buildPairTrend = ({ series, dateRange, granularity, typeCode, outcomeCode }) => {
  const period = previousPeriod(dateRange);
  if (!period) {
    return { rows: [], totals: { calls: 0, typeCalls: 0, previousCalls: 0, previousTypeCalls: 0 } };
  }

  const outcome = callsByDay(series?.outcome, outcomeCode);
  const type = callsByDay(series?.type, typeCode);
  const previousOutcome = callsByDay(series?.previousOutcome, outcomeCode);
  const previousType = callsByDay(series?.previousType, typeCode);

  const groups = new Map();
  rangeDays(dateRange).forEach((date) => {
    const key = bucketKey(date, granularity);
    groups.set(key, [...(groups.get(key) ?? []), date]);
  });

  const rows = [...groups].map(([bucket, dates]) => {
    const previousDates = dates.map((date) => subDays(date, period.days));
    const calls = sumDays(outcome, dates);
    const typeCalls = sumDays(type, dates);
    const previousCalls = sumDays(previousOutcome, previousDates);
    const previousTypeCalls = sumDays(previousType, previousDates);
    const firstLabel = format(previousDates[0], 'dd.MM');

    return {
      bucket,
      label: formatBucketLabel(bucket, granularity),
      calls,
      typeCalls,
      share: shareOf(calls, typeCalls),
      previousCalls,
      previousTypeCalls,
      previousShare: shareOf(previousCalls, previousTypeCalls),
      previousLabel: previousDates.length > 1
        ? `${firstLabel}–${format(previousDates[previousDates.length - 1], 'dd.MM')}`
        : firstLabel
    };
  });

  const total = (field) => rows.reduce((sum, row) => sum + row[field], 0);

  return {
    rows,
    totals: {
      calls: total('calls'),
      typeCalls: total('typeCalls'),
      previousCalls: total('previousCalls'),
      previousTypeCalls: total('previousTypeCalls')
    }
  };
};

const findScore = (scores, formCode) => scores?.find((score) => score.formCode === formCode) ?? null;

/** Средний балл формы: null — оценённых звонков нет, а не балл ноль. */
export const averageScore = (scores, formCode) => {
  const score = findScore(scores, formCode);
  return score && score.scoreCount > 0 ? score.scoreSum / score.scoreCount : null;
};

export const scoredCount = (scores, formCode) => findScore(scores, formCode)?.scoreCount ?? 0;

export const formatScore = (value) =>
  value === null ? 'Нет оценки' : `${value.toFixed(1).replace('.', ',')} / 10`;

/**
 * Формы, по которым в этом ответе есть оценки: выбирать остальные незачем. Первой идёт самая
 * оценённая, она и открывается: порядок каталога со срезом не связан, и на звонках о продажах
 * открывалась бы форма про адреса с парой случайных оценок.
 */
export const scoredForms = (breakdown) => {
  const employees = breakdown?.employees ?? [];

  return (breakdown?.forms ?? [])
    .map((form) => ({
      form,
      scored: employees.reduce((sum, employee) => sum + scoredCount(employee.scores, form.code), 0)
    }))
    .filter(({ scored }) => scored > 0)
    .sort((a, b) => b.scored - a.scored)
    .map(({ form }) => form);
};

export const resolveActiveForm = (forms, selectedCode) =>
  forms.some(({ code }) => code === selectedCode) ? selectedCode : forms[0]?.code ?? null;

/** Сотрудники по убыванию звонков среза; строка без оператора остаётся последней, как в ответе. */
export const sortEmployees = (employees) =>
  [...(employees ?? [])].sort(
    (a, b) =>
      Number(hasOperator(b)) - Number(hasOperator(a))
      || b.calls - a.calls
      || b.denominatorCalls - a.denominatorCalls
      || a.employeeName.localeCompare(b.employeeName, 'ru')
  );

/**
 * Ряды графика сотрудников. Сервер отдаёт только непустые периоды: пустой период — это ноль звонков,
 * а доля и балл в нём не определены и точки не дают.
 */
export const buildEmployeeTrend = ({ employees, dateRange, granularity, formCode }) => {
  const buckets = employees.map((employee) => new Map(employee.buckets.map((bucket) => [bucket.bucket, bucket])));

  return rangeBucketKeys(dateRange, granularity).map((bucket) => {
    const row = { bucket, label: formatBucketLabel(bucket, granularity) };
    employees.forEach((_, index) => {
      const point = buckets[index].get(bucket);
      const denominator = point?.denominatorCalls ?? 0;
      const score = averageScore(point?.scores, formCode);

      row[`calls_${index}`] = point?.calls ?? 0;
      row[`share_${index}`] = shareOf(point?.calls ?? 0, denominator);
      row[`score_${index}`] = score === null ? null : Math.round(score * 10) / 10;
      row[`scored_${index}`] = scoredCount(point?.scores, formCode);
    });
    return row;
  });
};

/**
 * Итог качества по форме. Лидер и отстающий считаются только среди настоящих сотрудников и только
 * когда им есть с кем сравниваться: при равных баллах отстающего нет.
 */
export const summarizeQuality = (employees, formCode, lowSampleThreshold) => {
  const rows = employees.map((employee) => ({
    employee,
    score: averageScore(employee.scores, formCode),
    scored: scoredCount(employee.scores, formCode),
    scoreSum: findScore(employee.scores, formCode)?.scoreSum ?? 0
  }));
  const ranked = rows
    .filter(({ employee, score }) => hasOperator(employee) && score !== null)
    .sort((a, b) => b.score - a.score);
  const totalScored = rows.reduce((sum, row) => sum + row.scored, 0);
  const totalScore = rows.reduce((sum, row) => sum + row.scoreSum, 0);
  const describe = (row) => ({ ...row, isLowSample: row.scored < lowSampleThreshold });
  const hasSpread = ranked.length > 1 && ranked[0].score > ranked[ranked.length - 1].score;

  return {
    totalScored,
    average: totalScored > 0 ? totalScore / totalScored : null,
    leader: hasSpread ? describe(ranked[0]) : null,
    laggard: hasSpread ? describe(ranked[ranked.length - 1]) : null
  };
};

export const formatCount = (value) => Number(value || 0).toLocaleString('ru-RU');

export const formatPercent = (part, total) =>
  total > 0 ? `${(Math.round((part / total) * 1000) / 10).toLocaleString('ru-RU')}%` : '—';

export const callsNoun = (value) => {
  const number = Math.abs(Number(value) || 0) % 100;
  if (number >= 11 && number <= 14) {
    return 'звонков';
  }

  const last = number % 10;
  if (last === 1) {
    return 'звонок';
  }

  return last >= 2 && last <= 4 ? 'звонка' : 'звонков';
};
